import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Upload, Trash2, Copy, Check } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/media`);
      setMedia(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast({
        title: "Error",
        description: "Failed to load media library",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/cms/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: "Success",
        description: "File uploaded successfully"
      });

      fetchMedia();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to upload file",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media file?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/cms/media/${mediaId}`);
      toast({
        title: "Success",
        description: "Media deleted successfully"
      });
      fetchMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast({
        title: "Error",
        description: "Failed to delete media",
        variant: "destructive"
      });
    }
  };

  const copyToClipboard = (url) => {
    const fullUrl = `${BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(url);
    toast({
      title: "Copied!",
      description: "URL copied to clipboard"
    });
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return <div className="text-center py-12">Loading media library...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-light text-[#2d2d2d]">Media Library</h1>
        <div>
          <input
            type="file"
            id="file-upload"
            accept="image/*,video/*"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <label htmlFor="file-upload" className={uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}>
            <span className="inline-flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors">
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Media'}
            </span>
          </label>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
        <p className="text-[#2d2d2d] font-light">
          <strong>Supported formats:</strong> JPEG, PNG, JPG, WebP (images), MP4 (videos)<br />
          <strong>Note:</strong> Images will be automatically optimized for web use.
        </p>
      </div>

      {media.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-lg">
          <p className="text-[#2d2d2d] font-light">No media files uploaded yet. Upload your first file!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {media.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-square bg-gray-100">
                {item.type === 'image' ? (
                  <img
                    src={`${BACKEND_URL}${item.url}`}
                    alt={item.filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <video
                    src={`${BACKEND_URL}${item.url}`}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="p-4">
                <p className="text-sm text-[#2d2d2d] font-light mb-2 truncate">{item.filename}</p>
                <p className="text-xs text-[#2d2d2d] mb-3">{(item.size / 1024).toFixed(2)} KB</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => copyToClipboard(item.url)}
                    className="flex-1 bg-[#0A1A50] hover:bg-[#060F2E] text-white"
                    size="sm"
                  >
                    {copiedId === item.url ? (
                      <><Check className="w-3 h-3 mr-1" /> Copied</>
                    ) : (
                      <><Copy className="w-3 h-3 mr-1" /> URL</>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleDelete(item.id)}
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    size="sm"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;